import { fromLonLat } from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import TileArcGIS from 'ol/source/TileArcGISRest';
import TileState from 'ol/TileState';
import { boundingExtent } from 'ol/extent';

import { LayerManager } from 'models';
import { getTileGrid } from 'utils';
import { MAP_PROJECTION, SA_BOUNDS } from 'config';

import { AbstractLayer, AbstractLayerProps, LayerProps } from './AbstractLayer';

export interface Props extends AbstractLayerProps {
    config: LayerManager.LayerConfig;
    url?: string;
}

/**
 * The MapServerTileLayer is a rasterlayer that connects to an ArcGIS Server
 * instance, in particular a MapService.
 */
export class MapServerTileLayer extends AbstractLayer<TileLayer<TileArcGIS>> {
    private date?: Date | null;

    private issueTime?: Date | null;

    private url: string | null;

    private source: TileArcGIS | null;

    constructor({ config, auth, opacity, visible, zIndex, url }: Props) {
        super({ config, opacity, visible, zIndex });
        this.auth = auth;
        this.url = url ?? null;
        this.source = this.buildSource();
    }

    initializeLayer(props: LayerProps) {
        return new TileLayer({
            ...props,
            source: this.source || undefined,
        });
    }

    applyAuthentication() {
        if (this.auth && this.source) {
            this.source.setTileLoadFunction((tile, src) => {
                const xhr = new XMLHttpRequest();
                xhr.responseType = 'blob';

                // Need it like this for the 'this' variable to work

                xhr.addEventListener('loadend', function () {
                    const data = this.response;
                    if (data !== undefined) {
                        try {
                            // @ts-ignore

                            tile.getImage().src = URL.createObjectURL(data);
                        } catch (e) {
                            console.error(e);
                            tile.setState(TileState.ERROR);
                        }
                    } else {
                        tile.setState(TileState.ERROR);
                    }
                });
                xhr.addEventListener('error', () => {
                    tile.setState(TileState.ERROR);
                });
                xhr.open('GET', src);

                // Since these requests are not going
                // to our server, we don't use our token.
                if (this.auth && this.config.tokenRequired)
                    xhr.setRequestHeader(
                        'Authorization',
                        `${this.auth.tokenType} ${this.auth.accessToken}`,
                    );

                xhr.send();
            });
        }
    }

    private buildSource() {
        if (this.url == null || this.config == null) return null;

        const SRS = MAP_PROJECTION.getCode();

        const coords = SA_BOUNDS;
        const stateExtent = boundingExtent([
            fromLonLat([coords.minLong, coords.minLat], SRS),
            fromLonLat([coords.maxLong, coords.minLat], SRS),
            fromLonLat([coords.maxLong, coords.maxLat], SRS),
            fromLonLat([coords.minLong, coords.minLat], SRS),
        ]);

        const source = new TileArcGIS({
            crossOrigin: 'anonymous',
            url: this.url,
            params: {
                LAYERS: this.config.id,
                TIME: this.date?.toISOString(),
                VERSION: '1.1.1',
                TILED: true,
                SRS,
            },
            tileGrid: getTileGrid(SRS, stateExtent),
        });

        this.source?.dispose();
        this.source = source;
        if (this.layer) this.layer?.setSource(source);

        this.applyAuthentication();
        return source;
    }
}

export default MapServerTileLayer;
